<script setup lang="ts">
import OfflineQueueNotice from '@/components/shell/OfflineQueueNotice.vue';
import { computed } from 'vue';
import { RouterLink, RouterView, useRoute } from 'vue-router';
import { useHomeStore } from '@/stores/home.store';
import { useSessionStore } from '@/stores/session.store';
import { SHELL_BREAKPOINTS, useShellBreakpoint } from '@/design-system';
import { navigableDestinations } from '@/router/destinations';
import ShellBanners from '@/components/shell/ShellBanners.vue';
import { useTheme } from '@/composables/theme';

const route = useRoute();
// `SHELL_BREAKPOINTS.tecnico` (<= 600px) — o limite do Técnico, publicado no Design System com o
// par TS/CSS. Não é `BREAKPOINTS.mobile` "por coincidir": um app não empresta o limite de outro.
const isMobile = useShellBreakpoint(SHELL_BREAKPOINTS.tecnico);
const home = useHomeStore();
const { session } = useSessionStore();
const { isDark, toggleTheme } = useTheme();

const railDestinations = computed(() => navigableDestinations);
const mobileDestinations = computed(() => navigableDestinations.filter((item) => !item.desktopOnly));
const firstName = computed(() => {
  const nome = session.value.user?.name ?? 'técnico';
  return nome.split(' ')[0] ?? nome;
});
const initials = computed(() => {
  const nome = session.value.user?.name ?? 'DT';
  const parts = nome.split(' ').filter(Boolean);
  const first = parts[0]?.charAt(0) ?? 'D';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? 'T') : 'T';
  return `${first}${last}`.toUpperCase();
});

const badgeCount = (name: string): number => {
  if (home.summary.kind !== 'data') return 0;
  const summary = home.summary.value;
  switch (name) {
    case 'disponiveis': return summary.availableOfferCount;
    case 'servicos': return summary.activeServiceCount;
    case 'agenda': return summary.agendaTodayCount + summary.agendaOverdueCount;
    case 'perfil': return summary.pendencies.length;
    default: return 0;
  }
};

// Só carrega a Home com sessão de técnico estabelecida — o guard do router garante o usuário,
// mas nunca se deve deixar uma corrida de sessão estourar uma rejeição no setup do shell.
if (home.summary.kind === 'idle' && session.value.user !== null) void home.load();
</script>

<template>
  <div
    class="app-shell"
    :class="{ 'app-shell--mobile': isMobile }"
  >
    <aside
      v-if="!isMobile"
      class="app-shell__rail"
    >
      <div class="app-shell__brand">
        <span class="app-shell__brand-name">Dentis<span>Tech</span></span>
        <span class="app-shell__brand-sub">Técnicos</span>
      </div>
      <nav
        class="app-shell__nav"
        aria-label="Navegação principal"
      >
        <RouterLink
          v-for="item in railDestinations"
          :key="item.name"
          :to="item.path"
          class="app-shell__link"
          active-class="app-shell__link--active"
        >
          <span
            class="app-shell__link-icon"
            aria-hidden="true"
          >{{ item.icon }}</span>
          <span class="app-shell__link-label">{{ item.title }}</span>
          <span
            v-if="badgeCount(item.name) > 0"
            class="app-shell__badge"
          >{{ badgeCount(item.name) }}</span>
        </RouterLink>
      </nav>
      <div class="app-shell__foot">
        <span
          class="app-shell__foot-dot"
          :class="{ 'app-shell__foot-dot--on': home.summary.kind === 'data' && home.summary.value.isAvailable }"
          aria-hidden="true"
        />
        <span>{{ home.summary.kind === 'data' && home.summary.value.isAvailable ? 'Disponível' : 'Indisponível' }}</span>
        <button
          type="button"
          class="app-shell__foot-btn"
          :aria-label="isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'"
          :title="isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'"
          @click="toggleTheme"
        >
          {{ isDark ? '☀️' : '🌙' }}
        </button>
      </div>
    </aside>

    <div class="app-shell__content">
      <header
        v-if="isMobile"
        class="app-shell__top app-shell__top--mobile"
      >
        <div class="app-shell__top-row">
          <template v-if="route.name === 'inicio'">
            <span class="app-shell__hello">Olá, {{ firstName }} 👋</span>
            <div
              class="app-shell__avatar"
              aria-hidden="true"
            >
              {{ initials }}
            </div>
          </template>
          <template v-else>
            <div class="app-shell__brand-name app-shell__brand-name--mobile">
              Dentis<span>Tech</span>
            </div>
          </template>
          <button
            type="button"
            class="app-shell__top-btn"
            :aria-label="isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'"
            :title="isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'"
            @click="toggleTheme"
          >
            {{ isDark ? '☀️' : '🌙' }}
          </button>
        </div>
      </header>

      <header
        v-if="!isMobile && route.name === 'inicio'"
        class="app-shell__top app-shell__top--desktop"
      >
        <div class="app-shell__top-row">
          <div class="app-shell__hello-block">
            <div class="app-shell__hello">
              Olá, {{ firstName }} 👋
            </div>
          </div>
          <button
            type="button"
            class="app-shell__top-btn"
            :aria-label="isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'"
            :title="isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'"
            @click="toggleTheme"
          >
            {{ isDark ? '☀️' : '🌙' }}
          </button>
          <button
            type="button"
            class="app-shell__bell"
            aria-label="Notificações"
          >
            🔔
          </button>
          <div
            class="app-shell__avatar"
            aria-hidden="true"
          >
            {{ initials }}
          </div>
        </div>
      </header>

      <!-- Banners do shell (PWA/avisos) — acima do conteúdo nos dois formatos (Especificação §9).
           Só com sessão: telas públicas não têm shell nem banners. -->


      <main class="app-shell__main">
        <div class="app-shell__inner">
          <ShellBanners v-if="session.user !== null" />
          <OfflineQueueNotice v-if="session.user !== null" />
          <RouterView v-slot="{ Component }">
            <Transition
              name="page-fade"
              mode="out-in"
            >
              <component :is="Component" />
            </Transition>
          </RouterView>
        </div>
      </main>
    </div>

    <nav
      v-if="isMobile"
      class="app-shell__bottom"
      aria-label="Navegação móvel"
    >
      <RouterLink
        v-for="item in mobileDestinations"
        :key="item.name"
        :to="item.path"
        class="app-shell__bottom-link"
        active-class="app-shell__bottom-link--active"
      >
        <span
          class="app-shell__bottom-icon"
          aria-hidden="true"
        >{{ item.icon }}</span>
        <span class="app-shell__bottom-label">{{ item.shortTitle ?? item.title }}</span>
        <span
          v-if="badgeCount(item.name) > 0"
          class="app-shell__dot"
        >{{ badgeCount(item.name) }}</span>
      </RouterLink>
    </nav>
  </div>
</template>

<style scoped>
.app-shell {
  display: flex;
  min-height: 100vh;
  min-height: 100dvh; /* Safari iOS: dvh acompanha a toolbar; vh fica como fallback */
  background: var(--dt-bg);
  color: var(--dt-text);
}

.app-shell__rail {
  position: sticky;
  top: 0;
  align-self: flex-start;
  height: 100vh;
  height: 100dvh;
  width: var(--dt-rail-w);
  flex: 0 0 var(--dt-rail-w);
  display: flex;
  flex-direction: column;
  background: var(--dt-rail);
}

.app-shell__brand {
  padding: var(--dt-rail-brand-padding);
  border-bottom: var(--dt-border-hairline) solid var(--dt-rail-line);
}

.app-shell__brand-name {
  display: block;
  font-family: var(--dt-font-family);
  font-size: var(--dt-rail-brand-name-size);
  font-weight: var(--dt-rail-brand-name-weight);
  letter-spacing: -0.02em;
  color: var(--dt-blue-on-dark); /* .rail-brand .bn do protótipo (styles.css:59) */
}

.app-shell__brand-name span {
  color: var(--dt-teal);
}

.app-shell__brand-sub {
  display: block;
  margin-top: var(--dt-space-xxs);
  font-family: var(--dt-font-family);
  font-size: var(--dt-rail-brand-sub-size);
  font-weight: var(--dt-rail-brand-sub-weight);
  letter-spacing: var(--dt-rail-brand-sub-letter-spacing);
  text-transform: var(--dt-rail-brand-sub-transform);
  color: var(--dt-rail-brand-sub-color);
}

.app-shell__nav {
  flex: 1;
  padding: var(--dt-space-sm) 0;
  display: flex;
  flex-direction: column;
}

.app-shell__link {
  display: flex;
  align-items: center;
  gap: var(--dt-rail-item-gap);
  width: 100%;
  min-height: var(--dt-rail-item-min-height);
  padding: var(--dt-rail-item-padding);
  border-left: var(--dt-border-thick) solid transparent;
  background: none;
  font-family: var(--dt-font-family);
  font-size: var(--dt-rail-item-font-size);
  font-weight: var(--dt-rail-item-font-weight);
  color: var(--dt-rail-text);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}

.app-shell__link:hover {
  background: var(--dt-rail-hover);
  color: var(--dt-rail-hover-text);
}

.app-shell__link--active {
  background: var(--dt-rail-item-on-bg);
  color: var(--dt-rail-text-strong);
  border-left-color: var(--dt-rail-item-on-border);
}

.app-shell__link:focus-visible {
  outline: var(--dt-focus-ring-width) solid var(--dt-teal);
  outline-offset: calc(-1 * var(--dt-focus-ring-width));
}

.app-shell__link-icon {
  width: var(--dt-rail-item-icon-width);
  text-align: center;
  font-size: var(--dt-rail-item-icon-size);
}

.app-shell__link-label {
  flex: 1;
}

.app-shell__badge {
  min-width: 19px;
  height: 18px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--dt-radius-badge);
  background: var(--dt-rail-badge-bg);
  color: var(--dt-rail-badge-color);
  font-size: var(--dt-rail-badge-size);
  font-weight: var(--dt-font-weight-extrabold);
}

.app-shell__foot {
  display: flex;
  align-items: center;
  gap: var(--dt-space-xs);
  padding: var(--dt-rail-foot-padding);
  border-top: var(--dt-border-hairline) solid var(--dt-rail-line);
  font-family: var(--dt-font-family);
  font-size: var(--dt-rail-foot-size);
  font-weight: var(--dt-rail-foot-weight);
  color: var(--dt-rail-foot-color);
}

.app-shell__foot-dot {
  width: 8px;
  height: 8px;
  border-radius: var(--dt-radius-full);
  background: var(--dt-error);
}

.app-shell__foot-dot--on {
  background: var(--dt-teal);
}

.app-shell__foot-btn {
  margin-left: auto;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
  border-radius: var(--dt-radius-xs);
  transition: transform var(--dt-motion-fast) var(--dt-motion-ease);
}

.app-shell__foot-btn:hover {
  transform: scale(1.15);
}

.app-shell__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.app-shell__top {
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--dt-surface);
  border-bottom: var(--dt-border-hairline) solid var(--dt-divider);
  padding: var(--dt-space-md) 16px;
}

/* Barra superior desktop do protótipo (.app.d .top, styles.css:38: padding 14px 26px). */
.app-shell__top--desktop {
  padding: 14px 26px;
}

.app-shell__top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--dt-space-xs);
}

.app-shell__hello-block {
  flex: 1;
  min-width: 0;
}

.app-shell__hello {
  font-size: var(--dt-font-size-lg);
  font-weight: var(--dt-font-weight-extrabold);
  letter-spacing: -0.02em;
  line-height: var(--dt-line-height-snug);
}

.app-shell__bell {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: var(--dt-surface-subtle);
  border: var(--dt-border-hairline) solid var(--dt-divider);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  flex-shrink: 0;
  transition: background var(--dt-motion-fast) var(--dt-motion-ease), border-color var(--dt-motion-fast) var(--dt-motion-ease), transform var(--dt-motion-fast) var(--dt-motion-ease);
}

.app-shell__bell:hover {
  background: var(--dt-surface-variant);
  border-color: var(--dt-border-strong);
  transform: scale(1.05);
}

/* Avatar com iniciais (styles.css:44: gradiente teal→navy, 32px, fonte 11px). */
.app-shell__avatar {
  border-radius: var(--dt-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--dt-font-weight-extrabold);
  color: var(--dt-avatar-color);
  background: var(--dt-avatar-bg);
  font-size: var(--dt-avatar-font-size);
  width: var(--dt-avatar-size);
  height: var(--dt-avatar-size);
  flex-shrink: 0;
}

.app-shell__top-btn {
  min-width: var(--dt-touch-target-min);
  min-height: var(--dt-touch-target-min);
  border: none;
  background: none;
  font-size: 16px;
  cursor: pointer;
  transition: transform var(--dt-motion-fast) cubic-bezier(0.34, 1.56, 0.64, 1);
}

.app-shell__top-btn:active {
  transform: scale(0.88) rotate(18deg);
}

.app-shell__main {
  flex: 1;
}

.app-shell__inner {
  max-width: var(--dt-content-max-width);
  margin: 0 auto;
  padding: 14px 16px 22px; /* .app.m .scroll (styles.css:46) */
}

.app-shell:not(.app-shell--mobile) .app-shell__inner {
  padding: 22px 26px 40px; /* .app.d .scroll (styles.css:47) */
}

.app-shell--mobile {
  height: 100%;
  height: 100dvh;
  max-height: 100dvh;
  flex-direction: column;
  overflow: hidden;
}

.app-shell--mobile .app-shell__content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.app-shell--mobile .app-shell__main {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  padding-bottom: var(--dt-space-md);
}

.app-shell__bottom {
  display: flex;
  align-items: center;
  background: var(--dt-nav-bg);
  border-top: var(--dt-nav-border-top);
  width: 100%;
  flex-shrink: 0;
  padding-bottom: max(6px, env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  z-index: 20;
}

.app-shell__bottom-link {
  flex: 1 1 0;
  min-width: 0;
  max-width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-height: 52px;
  padding: 6px 2px 5px;
  border: none;
  background: none;
  text-decoration: none;
  font-family: var(--dt-font-family);
  cursor: pointer;
  transition: transform var(--dt-motion-fast) var(--dt-motion-ease);
}

.app-shell__bottom-link:active {
  transform: scale(0.94);
}

.app-shell__bottom-icon {
  width: 24px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
  line-height: 1;
  transition: transform var(--dt-motion-fast) cubic-bezier(0.34, 1.56, 0.64, 1);
}

.app-shell__bottom-link--active .app-shell__bottom-icon {
  transform: scale(1.06);
}

.app-shell__bottom-label {
  font-size: 10px;
  line-height: 1.2;
  font-weight: var(--dt-font-weight-bold);
  color: var(--dt-nav-label-color);
  text-align: center;
  width: 100%;
  max-width: 100%;
  padding: 0 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  transition: color var(--dt-motion-fast) var(--dt-motion-ease);
}

.app-shell__bottom-link--active .app-shell__bottom-label {
  color: var(--dt-nav-label-on-color);
}

.app-shell__dot {
  position: absolute;
  top: 4px;
  right: calc(50% - 16px);
  min-width: 16px;
  height: 16px;
  padding: 0 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--dt-radius-badge, 20px);
  background: var(--dt-nav-dot-bg);
  color: var(--dt-nav-dot-color);
  font-size: 9px;
  font-weight: var(--dt-font-weight-extrabold);
  line-height: 1;
}

/* Transição suave entre páginas/rotas */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.16s ease, transform 0.16s cubic-bezier(0.16, 1, 0.3, 1);
}
.page-fade-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
.page-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Perfil do técnico: o rail mostra o dono da sessão no rodapé (sem botão de sair no mobile —
   o Perfil (TEL-12) terá o "Sair da conta"). */
.app-shell__foot-logout {
  display: none;
}

.app-shell__brand-name--mobile {
  font-size: 16px;
  font-weight: var(--dt-font-weight-extrabold);
  letter-spacing: -0.02em;
  color: var(--dt-blue);
}

[data-theme="dark"] .app-shell__brand-name--mobile {
  color: var(--dt-blue-on-dark);
}

.app-shell__brand-name--mobile span {
  color: var(--dt-teal);
}

/* Micro-animações globais do shell: elevação em cards e feedback de clique em botões */
:deep(.dt-card) {
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

:deep(.dt-card:hover) {
  transform: translateY(-2px);
  box-shadow: var(--dt-shadow-md);
}

:deep(.dt-btn) {
  transition: transform var(--dt-motion-fast) var(--dt-motion-ease), background var(--dt-motion-fast) var(--dt-motion-ease), border-color var(--dt-motion-fast) var(--dt-motion-ease);
}

:deep(.dt-btn:active) {
  transform: scale(0.98);
}
</style>
